import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Input,
  Select,
  Spinner,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { getBackendURL } from "./env.js";

const WEEKDAY_HEADERS = ["S", "M", "T", "W", "T", "F", "S"];

const csrf = () =>
  document.cookie.split("csrftoken=")[1]?.split(";")[0] || "";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (s) => EMAIL_RE.test((s || "").trim());

// All slots are interpreted in this timezone, regardless of the booker's
// local timezone. "2:00 PM" on the calendar always means 2:00 PM ET.
const BOOKING_TIMEZONE = "America/New_York";

// Read the offset (in minutes) of the given timezone at the given UTC instant.
const getTzOffsetMinutes = (utcDate, timeZone) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "shortOffset",
    hour: "2-digit",
    hour12: false,
  });
  const parts = dtf.formatToParts(utcDate);
  const offsetStr =
    parts.find((p) => p.type === "timeZoneName")?.value || "GMT+0";
  const m = offsetStr.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
  if (!m) return 0;
  const sign = m[1] === "-" ? -1 : 1;
  const h = parseInt(m[2], 10);
  const mn = parseInt(m[3] || "0", 10);
  return sign * (h * 60 + mn);
};

// Treat (y, m, d, hh, mm) as a wall-clock time in the booking timezone (ET)
// and return the equivalent UTC ISO string. Two iterations to converge across
// DST transitions.
const etWallClockToUtcIso = (y, m, d, hh, mm) => {
  const tentativeMs = Date.UTC(y, m, d, hh, mm);
  let offsetMin = getTzOffsetMinutes(new Date(tentativeMs), BOOKING_TIMEZONE);
  let correctedMs = tentativeMs - offsetMin * 60 * 1000;
  // Re-check: at the corrected UTC instant, is the offset still the same?
  // (Handles DST boundaries — usually a no-op.)
  const recheckOffset = getTzOffsetMinutes(new Date(correctedMs), BOOKING_TIMEZONE);
  if (recheckOffset !== offsetMin) {
    correctedMs = tentativeMs - recheckOffset * 60 * 1000;
  }
  return new Date(correctedMs).toISOString();
};

const fmtDateKey = (d) => {
  // Return YYYY-MM-DD in local time so it lines up with the slot map keys.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const fmtTime12h = (hhmm) => {
  const [hh, mm] = hhmm.split(":");
  const h = parseInt(hh, 10);
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${mm} ${period}`;
};

const fmtMonthYear = (d) =>
  d.toLocaleDateString(undefined, { month: "long", year: "numeric" });

const buildMonthGrid = (anchor) => {
  // Return 42 Date objects (6 weeks × 7 days) starting on the Sunday before the 1st.
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay()); // back up to Sunday
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Tab 2 of the Help Desk modal — calendar + slot picker + booking form.
 * Fetches /api/meeting-bookings/slots/ for the visible month, lets the user
 * pick a free 30-minute slot, then POSTs to /api/meeting-bookings/ to create
 * the Zoho meeting + send invites.
 */
const MeetingBookingTab = ({
  isSignedIn,
  authChecked,
  prefillEmail = "",
  prefillName = "",
  prefillRole = "",
  onClose,
  // When set, the calendar shows only this owner's availability and the
  // booking is assigned to them (personal booking link). Omitted on the
  // shared help-desk page, which pools every owner.
  ownerSlug = "",
}) => {
  const toast = useToast();

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const [anchor, setAnchor] = useState(today);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [email, setEmail] = useState(prefillEmail);
  const [name, setName] = useState(prefillName);
  const [role, setRole] = useState(prefillRole);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Re-prefill when the auth check finishes
  useEffect(() => {
    if (authChecked && isSignedIn) {
      if (prefillEmail) setEmail(prefillEmail);
      if (prefillName) setName(prefillName);
      if (prefillRole) setRole(prefillRole);
    }
  }, [authChecked, isSignedIn, prefillEmail, prefillName, prefillRole]);

  const cells = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const fromDate = cells[0];
  const toDate = cells[cells.length - 1];

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    try {
      const fromKey = fmtDateKey(fromDate);
      const toKey = fmtDateKey(toDate);
      const ownerParam = ownerSlug
        ? `&owner=${encodeURIComponent(ownerSlug)}`
        : "";
      const res = await fetch(
        `${getBackendURL()}/api/meeting-bookings/slots/?from=${fromKey}&to=${toKey}${ownerParam}`,
        { credentials: "include" },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || `HTTP ${res.status}`);
      setSlotsByDate(j.slots_by_date || {});
    } catch (err) {
      toast({
        title: "Could not load availability",
        description: err.message,
        status: "error",
        duration: 5000,
      });
      setSlotsByDate({});
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, ownerSlug, toast]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const handlePrevMonth = () => {
    const next = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
    setAnchor(next);
    setSelectedDate(null);
    setSelectedSlot("");
  };

  const handleNextMonth = () => {
    const next = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
    setAnchor(next);
    setSelectedDate(null);
    setSelectedSlot("");
  };

  const handleDayClick = (d, key, hasSlots) => {
    if (!hasSlots) return;
    setSelectedDate(d);
    setSelectedSlot("");
  };

  const slotsForSelected = selectedDate
    ? slotsByDate[fmtDateKey(selectedDate)] || []
    : [];

  const handleSubmit = async () => {
    if (!selectedDate || !selectedSlot) {
      toast({ title: "Pick a date and time", status: "error", duration: 3000 });
      return;
    }
    const e = email.trim();
    const m = message.trim();
    if (!e) {
      toast({ title: "Email is required", status: "error", duration: 3000 });
      return;
    }
    if (!isValidEmail(e)) {
      toast({
        title: "That doesn't look like a valid email",
        description: "Use the format name@domain.com.",
        status: "error",
        duration: 4000,
      });
      return;
    }
    if (!m) {
      toast({
        title: "Please tell us what the meeting is about",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // The slot label "14:00" is interpreted as ET wall-clock time. We convert
    // it to the corresponding UTC instant so the backend can store it as
    // TIMESTAMPTZ and the entire chain (slot dedup, Zoho meeting, calendar
    // invite) stays on the same ET basis.
    const [hh, mm] = selectedSlot.split(":");
    const utcIso = etWallClockToUtcIso(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      parseInt(hh, 10),
      parseInt(mm, 10),
    );

    setSubmitting(true);
    try {
      const res = await fetch(`${getBackendURL()}/api/meeting-bookings/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrf(),
        },
        body: JSON.stringify({
          scheduled_at: utcIso,
          user_email: e,
          user_name: name.trim(),
          user_role: role.trim(),
          message: m,
          ...(ownerSlug ? { owner_slug: ownerSlug } : {}),
        }),
      });
      const j = await res.json();
      if (!res.ok && res.status !== 207) {
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      const partial = res.status === 207;
      toast({
        title: partial ? "Meeting booked (with a hiccup)" : "Meeting booked!",
        description: partial
          ? j.warning || "Booking saved; you'll get a follow-up shortly."
          : "Check your inbox for the calendar invite.",
        status: partial ? "warning" : "success",
        duration: 6000,
        isClosable: true,
      });
      // Refresh slots so the just-booked time disappears
      await fetchSlots();
      setSelectedSlot("");
      setMessage("");
      if (onClose) onClose();
    } catch (err) {
      toast({
        title: "Could not book the meeting",
        description: err.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canGoBack = anchor.getFullYear() > today.getFullYear()
    || (anchor.getFullYear() === today.getFullYear()
        && anchor.getMonth() > today.getMonth());

  return (
    <VStack spacing={4} align="stretch" py={2}>
      {/* Calendar header */}
      <HStack justify="space-between">
        <IconButton
          size="sm"
          variant="ghost"
          aria-label="Previous month"
          icon={<ChevronLeftIcon />}
          onClick={handlePrevMonth}
          isDisabled={!canGoBack}
        />
        <Text fontWeight="bold">{fmtMonthYear(anchor)}</Text>
        <IconButton
          size="sm"
          variant="ghost"
          aria-label="Next month"
          icon={<ChevronRightIcon />}
          onClick={handleNextMonth}
        />
      </HStack>

      {/* Weekday header */}
      <Grid templateColumns="repeat(7, 1fr)" gap={1}>
        {WEEKDAY_HEADERS.map((w, i) => (
          <GridItem key={`wh-${i}`} textAlign="center">
            <Text fontSize="xs" color="gray.500">{w}</Text>
          </GridItem>
        ))}
      </Grid>

      {/* Day grid */}
      {loading ? (
        <HStack justify="center" py={6}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="gray.600">Loading availability…</Text>
        </HStack>
      ) : (
        <Grid templateColumns="repeat(7, 1fr)" gap={1}>
          {cells.map((d, idx) => {
            const inMonth = d.getMonth() === anchor.getMonth();
            const isPast = d < today;
            const key = fmtDateKey(d);
            const hasSlots = (slotsByDate[key] || []).length > 0;
            const clickable = !isPast && hasSlots;
            const selected = selectedDate && isSameDay(d, selectedDate);
            const opacity = clickable
              ? 1
              : (inMonth ? 0.35 : 0.2);
            return (
              <GridItem key={`d-${idx}`}>
                <Button
                  size="sm"
                  width="100%"
                  variant={selected ? "solid" : "ghost"}
                  colorScheme={selected ? "red" : (clickable ? "gray" : undefined)}
                  isDisabled={!clickable}
                  opacity={opacity}
                  onClick={() => handleDayClick(d, key, clickable)}
                  fontWeight={isSameDay(d, today) ? "bold" : "normal"}
                >
                  {d.getDate()}
                </Button>
              </GridItem>
            );
          })}
        </Grid>
      )}

      {/* Slot picker */}
      {selectedDate && (
        <Box>
          <Text fontSize="sm" color="gray.700" mb={2}>
            Available times on{" "}
            <strong>
              {selectedDate.toLocaleDateString(undefined, {
                weekday: "long",
                month: "short",
                day: "numeric",
              })}
            </strong>{" "}
            <Text as="span" color="gray.500">
              (Eastern Time)
            </Text>
          </Text>
          {slotsForSelected.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              No times available on this day.
            </Text>
          ) : (
            <Grid templateColumns="repeat(auto-fill, minmax(96px, 1fr))" gap={2}>
              {slotsForSelected.map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={selectedSlot === s ? "solid" : "outline"}
                  colorScheme="red"
                  onClick={() => setSelectedSlot(s)}
                >
                  {fmtTime12h(s)} ET
                </Button>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Booking form (only after a slot is picked) */}
      {selectedSlot && (
        <VStack spacing={3} align="stretch" pt={2}>
          <FormControl
            isRequired
            isInvalid={Boolean(email.trim()) && !isValidEmail(email)}
          >
            <FormLabel>Your email</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              isDisabled={submitting}
            />
            {Boolean(email.trim()) && !isValidEmail(email) && (
              <Text fontSize="xs" color="red.500" mt={1}>
                Please enter a valid email (e.g. name@domain.com).
              </Text>
            )}
          </FormControl>
          <FormControl isRequired={authChecked && !isSignedIn}>
            <FormLabel>Your name</FormLabel>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              isDisabled={submitting}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Your role (optional)</FormLabel>
            <Input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="e.g. Doctor, Student, Admin"
              isDisabled={submitting}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>What is the meeting about?</FormLabel>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="A few sentences explaining why you'd like to meet."
              rows={4}
              isDisabled={submitting}
            />
          </FormControl>
          <Button
            colorScheme="red"
            onClick={handleSubmit}
            isLoading={submitting}
            loadingText="Booking"
          >
            Schedule meeting
          </Button>
        </VStack>
      )}
    </VStack>
  );
};

export default MeetingBookingTab;
